import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Plus, MapPin, Mail, ArrowUpRight } from 'lucide-react';
import api from '../lib/api';
import { PageHeader } from '../components/layout/PageHeader';
import { EmptyState } from '../components/layout/EmptyState';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { EventStatusBadge } from '../components/events/EventStatusBadge';
import { formatDateLong, formatTime12, zoneAbbr } from '../lib/time';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Drafts' },
  { key: 'past', label: 'Past' },
];

const effStatus = (ev) => ev.effective_status || ev.status || 'published';

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/events').then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const counts = events.reduce((acc, ev) => {
    const s = effStatus(ev);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const filtered = filter === 'all' ? events : events.filter((ev) => effStatus(ev) === filter);

  return (
    <div data-testid="events-page">
      <PageHeader title="Events" subtitle="Design flyers, send invites, and track RSVPs.">
        <Button onClick={() => navigate('/app/events/new')} className="gap-2" data-testid="events-create-button">
          <Plus className="h-4 w-4" /> Create Event
        </Button>
      </PageHeader>

      {!loading && events.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-5" data-testid="events-filter-bar">
          {FILTERS.map((f) => {
            const count = f.key === 'all' ? events.length : (counts[f.key] || 0);
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`events-filter-${f.key}`}
                className={`px-3.5 h-9 rounded-full text-sm font-medium border transition-colors ${
                  active ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-secondary'
                }`}
              >
                {f.label} <span className={active ? 'opacity-80' : 'opacity-60'}>({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-2xl" />)}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card">
          <EmptyState icon={CalendarDays} title="No events yet"
            description="Create your first event to design a beautiful flyer and invite your contacts."
            action={<Button onClick={() => navigate('/app/events/new')} className="gap-2"><Plus className="h-4 w-4" /> Create Event</Button>} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="events-filter-empty">
          No {filter} events.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((ev) => (
            <button key={ev.id} onClick={() => navigate(`/app/events/${ev.id}`)}
              className="group text-left rounded-2xl border border-border bg-card overflow-hidden shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)] hover:-translate-y-0.5 transition-transform"
              data-testid="event-card">
              <div className="h-24 relative" style={{ background: `linear-gradient(135deg, ${ev.theme_color || '#2F6B57'}, ${ev.accent_color || '#C86B4A'})` }}>
                {ev.cover_image && <img src={ev.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
                <div className="absolute top-3 right-3"><EventStatusBadge status={effStatus(ev)} /></div>
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-semibold line-clamp-1">{ev.title}</h3>
                <div className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 tabular-nums"><CalendarDays className="h-4 w-4" /> {ev.date ? formatDateLong(ev.date) : 'Date TBA'} {ev.time && `· ${formatTime12(ev.time)} ${zoneAbbr(ev.timezone)}`}</div>
                  {(ev.location_name || ev.address) && <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> <span className="line-clamp-1">{ev.location_name || ev.address}</span></div>}
                  <div className="flex items-center gap-2"><Mail className="h-4 w-4" /> {ev.invites_sent} invited</div>
                </div>
                <div className="mt-4 flex items-center gap-1 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Open flyer <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
